import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  getEpaycoPublicConfig,
  loadEpaycoScript,
  openEpaycoCheckout,
  getEpaycoWebhookUrl,
  getEpaycoResponseUrl,
  type EpaycoPayPayload,
} from '@/services/gateways';

type Phase = 'waiting' | 'preparing' | 'ready' | 'error';

interface EpaycoPayState {
  payload?: EpaycoPayPayload;
}

/**
 * Página intermedia del pago con ePayco.
 *
 * Se usa de dos formas:
 * - En una ventana/pestaña externa (modo 'popup'): el checkout de ePayco corre
 *   en un contexto de nivel superior, evitando los bloqueos del modal cuando la
 *   tienda está dentro de un iframe o entorno restringido.
 * - Como respaldo en la misma pestaña (si no se pudo abrir la ventana externa).
 *
 * La configuración sensible (llaves privadas) NUNCA llega aquí: solo recibimos
 * los datos del pedido y usamos la PUBLIC_KEY pública de ePayco.
 */
export default function EpaycoPayPage() {
  const location = useLocation();
  const statePayload = (location.state as EpaycoPayState | null)?.payload ?? null;

  const [payload, setPayload] = useState<EpaycoPayPayload | null>(statePayload);
  const [phase, setPhase] = useState<Phase>(statePayload ? 'preparing' : 'waiting');
  const [errorMsg, setErrorMsg] = useState('');
  const startedRef = useRef(false);

  // Si no recibimos los datos por navegación, los pedimos a la ventana que nos
  // abrió mediante un intercambio postMessage.
  useEffect(() => {
    if (statePayload || payload) return;
    if (!window.opener) {
      setPhase('error');
      setErrorMsg('No recibimos los datos del pedido. Vuelve a la tienda e inténtalo de nuevo.');
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; payload?: EpaycoPayPayload } | null;
      if (data?.type === 'epayco-pay-data' && data.payload) {
        setPayload(data.payload);
        setPhase('preparing');
        window.removeEventListener('message', onMessage);
      }
    };

    window.addEventListener('message', onMessage);
    // Le avisamos a la ventana que nos abrió que ya estamos listos para recibir.
    window.opener.postMessage({ type: 'epayco-pay-ready' }, window.location.origin);

    return () => window.removeEventListener('message', onMessage);
  }, [statePayload, payload]);

  // Cuando ya tenemos los datos, abrimos el checkout de ePayco.
  useEffect(() => {
    if (!payload || startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    async function run() {
      try {
        const cfg = await getEpaycoPublicConfig();
        if (cancelled) return;
        if (!cfg.configured) {
          throw new Error('ePayco no está configurado o está deshabilitado.');
        }
        await loadEpaycoScript();
        if (cancelled) return;
        openEpaycoCheckout({
          publicKey: cfg.publicKey,
          isTestMode: cfg.isTestMode,
          orderNumber: payload!.orderNumber,
          orderId: payload!.orderId,
          amount: payload!.amount,
          description: payload!.description,
          customerName: payload!.customerName,
          customerEmail: payload!.customerEmail,
          customerPhone: payload!.customerPhone,
          customerAddress: payload!.customerAddress,
          customerDocument: payload!.customerDocument,
          confirmationUrl: getEpaycoWebhookUrl(),
          responseUrl: getEpaycoResponseUrl(),
          display: 'onpage',
        });
        if (!cancelled) setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : 'No se pudo iniciar el pago.');
        setPhase('error');
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  return (
    <div className="relative z-0 min-h-screen bg-background-50 flex items-center justify-center px-4 py-10">
      <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
      <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 p-6 md:p-8 text-center">
        {phase === 'waiting' || phase === 'preparing' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-loader-4-line animate-spin text-accent-600 text-3xl"></i>
            </div>
            <h1 className="text-lg md:text-xl font-bold text-foreground-950 mb-2">
              Preparando pago seguro...
            </h1>
            <p className="text-foreground-600 text-sm">
              Estamos abriendo el checkout seguro de <strong>ePayco</strong>. No cierres esta ventana.
            </p>
          </>
        ) : phase === 'ready' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-shield-check-line text-emerald-600 text-3xl"></i>
            </div>
            <h1 className="text-lg md:text-xl font-bold text-foreground-950 mb-2">
              Checkout de ePayco abierto
            </h1>
            <p className="text-foreground-600 text-sm mb-6">
              Completa el pago en la ventana segura de ePayco. Al terminar, esta ventana te mostrará el resultado.
            </p>
            <button
              type="button"
              onClick={() => window.close()}
              className="inline-flex items-center justify-center gap-2 w-full py-3 bg-white border border-background-300 text-foreground-700 rounded-xl font-semibold text-sm hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
              Cerrar ventana
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-error-warning-line text-red-600 text-3xl"></i>
            </div>
            <h1 className="text-lg md:text-xl font-bold text-foreground-950 mb-2">
              No pudimos iniciar el pago
            </h1>
            <p className="text-foreground-600 text-sm mb-6">{errorMsg}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer mb-3"
            >
              <i className="ri-refresh-line text-lg"></i>
              Reintentar
            </button>
            <Link
              to="/checkout"
              className="inline-flex items-center justify-center gap-2 w-full py-3 bg-white border border-background-300 text-foreground-700 rounded-xl font-semibold text-sm hover:bg-background-50 transition-colors whitespace-nowrap"
            >
              Volver al checkout
            </Link>
          </>
        )}
      </div>
    </div>
  );
}